import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { loadRecoveryCode } from '../auth';
import { db } from '../../lib/firebase';

/**
 * ユーザー設定(オンボーディング)の完了度 — Firestore membership ベース判定。
 * - 'loading'       : uid / districtId 未確定 or 判定中
 * - 'unstarted'     : membership 無し or 同意/ニックネーム未設定 (新規参加が必要)
 * - 'needs-recovery': 参加済だが復帰コード未取得 (recovery/issue が必要)
 * - 'complete'      : 参加済 + 復帰コードあり (本体利用可)
 *
 * 注: 旧 `useOnboardingStatus`(localStorage の完了フラグ) は AppHome の gate 廃止に伴い未使用。
 * 本フックは EntryPage と同一基準を Firestore membership で判定し、RequireOnboarding が消費する。
 */
export type UserSetupStatus = 'loading' | 'unstarted' | 'needs-recovery' | 'complete';

/**
 * 地区メンバーシップを確認し、ユーザー設定(オンボーディング)の完了度を返す。
 *
 * 判定基準は EntryPage と同一:
 *   - membership に `consentAcceptedAt && nickname` があれば「参加済」
 *   - さらに `recoveryCodeHash`(membership) または localStorage の発行済コードがあれば 'complete'
 *   - 参加済だが復帰コードが無ければ 'needs-recovery'
 *   - membership 無し / 未参加なら 'unstarted'
 */
export function useUserSetupStatus(uid: string | null, districtId: string | null): UserSetupStatus {
  const [status, setStatus] = useState<UserSetupStatus>('loading');

  useEffect(() => {
    let cancelled = false;
    if (!uid || !districtId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStatus('loading');
      return;
    }
    setStatus('loading');
    void getDoc(doc(db, 'participants', uid, 'memberships', districtId))
      .then((snap) => {
        if (cancelled) return;
        if (snap.exists()) {
          const d = snap.data();
          if (d.consentAcceptedAt && d.nickname) {
            const hasLocalCode = loadRecoveryCode(districtId) !== null;
            setStatus(!d.recoveryCodeHash && !hasLocalCode ? 'needs-recovery' : 'complete');
            return;
          }
        }
        setStatus('unstarted');
      })
      .catch(() => {
        // 取得失敗時は未設定扱い (入口へ誘導してやり直させる)。
        if (!cancelled) setStatus('unstarted');
      });
    return () => {
      cancelled = true;
    };
  }, [uid, districtId]);

  return status;
}
